import React, { useState, useEffect } from 'react'
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { authApi } from '../api/auth'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

const createResetPasswordSchema = () => z.object({
  nova_senha: z.string().min(6, 'A senha deve ter no mínimo 6 caracteres'),
  confirmar_senha: z.string().min(6, 'A senha deve ter no mínimo 6 caracteres'),
}).refine((data) => data.nova_senha === data.confirmar_senha, {
  message: 'As senhas não coincidem',
  path: ['confirmar_senha'],
})

type ResetPasswordFormData = z.infer<ReturnType<typeof createResetPasswordSchema>>

const ResetPasswordPage: React.FC = () => {
  const resetPasswordSchema = React.useMemo(() => createResetPasswordSchema(), [])
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (!token) {
      setError('Token de recuperação não encontrado. Solicite uma nova recuperação de senha.')
    }
  }, [token])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  })

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      setError('Token de recuperação não encontrado.')
      return
    }

    setError(null)
    setIsLoading(true)
    
    try {
      await authApi.resetPassword(token, data.nova_senha)
      setSuccess(true)
      setTimeout(() => {
        navigate('/login')
      }, 3000)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao redefinir senha')
    } finally {
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            Senha redefinida com sucesso! Redirecionando para o login...
          </Alert>
          <Box sx={{ textAlign: 'center' }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/login')}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}
            >
              <ArrowBackIcon fontSize="small" />
              Ir para login
            </Link>
          </Box>
        </Paper>
      </Container>
    )
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/login')}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
          >
            <ArrowBackIcon fontSize="small" />
            Voltar para login
          </Link>
        </Box>

        <Typography variant="h4" component="h1" gutterBottom align="center">
          Redefinir senha
        </Typography>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Digite sua nova senha abaixo.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField
            {...register('nova_senha')}
            label="Nova senha"
            type="password"
            fullWidth
            margin="normal"
            error={!!errors.nova_senha}
            helperText={errors.nova_senha?.message}
            disabled={isLoading || !token}
            autoFocus
          />

          <TextField
            {...register('confirmar_senha')}
            label="Confirmar nova senha"
            type="password"
            fullWidth
            margin="normal"
            error={!!errors.confirmar_senha}
            helperText={errors.confirmar_senha?.message}
            disabled={isLoading || !token}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3, mb: 2 }}
            disabled={isLoading || !token}
          >
            {isLoading ? 'Redefinindo...' : 'Redefinir senha'}
          </Button>
        </form>
      </Paper>
    </Container>
  )
}

export default ResetPasswordPage



